// frontend/src/components/detail/tables/ConsideredSolutionsTable.jsx
import React from 'react';
import { Box, Typography, IconButton, Tooltip, Link as MuiLink } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { F } from '../../../utils/qualFmt';
import { slugify } from '../../../utils/slug';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { DataTable } from '../../tablekit';

/* ---------------- width map ---------------- */
const W = {
  parent: {
    name: { minWidth: 260 },
    occurrences: { width: 140, align: 'right' },
    open: { width: 64, align: 'center' }
  },
  reasons: {
    reason: { minWidth: 320 },
    avg: { width: 180, align: 'right' },
    count: { width: 140, align: 'right' },
    open: { width: 64, align: 'center' }
  },
  interviewsWithReason: {
    name: { width: 160 },
    date: { width: 120 },
    consideredRaw: { minWidth: 220 },
    reason: { minWidth: 280 },
    importance: { width: 160, align: 'right' },
    justification: { minWidth: 320 },
    quote: { minWidth: 320 },
    open: { width: 64, align: 'center' }
  },
  allInterviews: {
    name: { width: 160 },
    date: { width: 120 },
    consideredRaw: { minWidth: 220 },
    reasonsList: { minWidth: 320 },
    open: { width: 64, align: 'center' }
  }
};

/* ---------------- helpers ---------------- */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function consideredItems(story) {
  const arr = story?.considered_solutions;
  return Array.isArray(arr) ? arr : [];
}
function consideredCat(item) {
  return item?.considered_solution_category || item?.considered_solution || '';
}
function consideredRaw(item) {
  return item?.considered_solution || item?.considered_solution_text || '';
}
function reasonsArr(item) {
  const arr = item?.reasons_not_chosen;
  return Array.isArray(arr) ? arr : [];
}
function reasonCat(r) {
  return r?.reason_not_chosen_category || r?.reason_not_chosen || r?.category || '';
}
function reasonRaw(r) { return r?.reason_not_chosen || r?.reason || r?.text || ''; }
const reasonImportance = (r) => num(r?.reason_not_chosen_importance);
const reasonJust = (r) => r?.reason_not_chosen_importance_justification || '';
const reasonQuote = (r) => r?.reason_not_chosen_quote || '';

/** Build parents with nested arrays for the two child tables:
 *  - reasons: [{ category, avg, count, instances:[{...interview row...}] }]
 *  - interviews: [{ name, date, consideredRaw, reasonsList[], href }]
 */
function buildGroups(productStories = []) {
  const groups = new Map();

  for (const s of productStories) {
    const items = consideredItems(s);
    if (!items.length) continue;

    for (const it of items) {
      const cat = String(consideredCat(it) || '').trim();
      if (!cat) continue;

      const consSlug = slugify(cat);
      if (!groups.has(consSlug)) {
        groups.set(consSlug, {
          key: consSlug,
          name: cat,
          occurrences: 0,
          reasons: new Map(),
          interviews: []
        });
      }
      const g = groups.get(consSlug);
      g.occurrences += 1;

      const rs = reasonsArr(it);

      // Bucket reasons and collect interview rows for each reason
      for (const r of rs) {
        const rc = String(reasonCat(r) || '').trim() || 'Uncategorized';
        const rslug = slugify(rc);
        if (!g.reasons.has(rslug)) {
          g.reasons.set(rslug, { category: rc, sum: 0, n: 0, count: 0, instances: [] });
        }
        const bucket = g.reasons.get(rslug);
        const imp = reasonImportance(r);
        if (imp != null) { bucket.sum += imp; bucket.n += 1; }
        bucket.count += 1;

        bucket.instances.push({
          key: `${consSlug}-${rslug}-${s.customer_name || ''}-${s.conversation_date || ''}`,
          name: s.customer_name || 'Interview',
          date: s.conversation_date || '',
          consideredRaw: consideredRaw(it) || cat,
          reason: reasonRaw(r) || '',
          importance: imp,
          justification: reasonJust(r) || '',
          quote: reasonQuote(r) || '',
          href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        });
      }

      // All-interviews table rows
      g.interviews.push({
        key: `${consSlug}-all-${s.customer_name || ''}-${s.conversation_date || ''}`,
        name: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        consideredRaw: consideredRaw(it) || cat,
        reasonsList: rs.map(rr => reasonRaw(rr)).filter(Boolean),
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      });
    }
  }

  // finalize → arrays
  const parents = [...groups.values()].map(g => ({
    key: g.key,
    name: g.name,
    occurrences: g.occurrences,
    reasons: [...g.reasons.values()]
      .map(rc => ({
        key: `${g.key}-${slugify(rc.category)}`,
        category: rc.category,
        avg: rc.n ? rc.sum / rc.n : null,
        count: rc.count,
        instances: rc.instances.sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0)),
      }))
      .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category)),
    interviews: g.interviews.sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0)),
  }))
  .sort((a, b) => b.occurrences - a.occurrences || a.name.localeCompare(b.name));

  return parents;
}

/* ---------------- component (Table Kit) ---------------- */
export default function ConsideredSolutionsTable({ customerStories = [], title = 'Considered Solutions' }) {
  const location = useLocation();
  const rows = React.useMemo(() => buildGroups(customerStories), [customerStories]);

  if (!rows.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No considered solutions found for this product.</Typography>;
  }

  /* -------- Parent columns -------- */
  const columns = [
    {
      key: 'name',
      header: 'Considered Solution',
      render: (v, r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>,
      ...W.parent.name
    },
    { key: 'occurrences', header: 'Occurrences', type: 'number', align: 'right', ...W.parent.occurrences },
    {
      key: 'open',
      header: 'Open',
      accessor: (r) => `/considered-solution/${slugify(r.name)}${location.search || ''}`,
      align: 'center',
      render: (href) => (
        <Tooltip title="Open considered solution details">
          <IconButton size="small" component={RouterLink} to={href} aria-label="Open considered solution details">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      ...W.parent.open
    }
  ];

  /* -------- Sub-table #1: Reasons Not Chosen (expand each reason row to show its OWN table) -------- */
  const reasonsColumns = [
    { key: 'category', header: 'Reason Not Chosen', accessor: (r) => r.category || '—', ...W.reasons.reason },
    {
      key: 'avg',
      header: 'Avg Importance',
      type: 'number',
      align: 'right',
      accessor: (r) => (Number.isFinite(r?.avg) ? r.avg : null),
      render: (v) => (v != null ? F.reasonNotChosenImportance(v).text : '—'),
      ...W.reasons.avg
    },
    { key: 'count', header: 'Occurrences', type: 'number', align: 'right', ...W.reasons.count },
    {
      key: 'open',
      header: 'Open',
      accessor: (r, parent) => `/reason-not-chosen/${slugify(r.category || '')}${location.search || ''}`,
      align: 'center',
      render: (href) => (
        <Tooltip title="Open reason details">
          <IconButton size="small" component={RouterLink} to={href} aria-label="Open reason details">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      ...W.reasons.open
    }
  ];

  // When a reason row is expanded, render a SEPARATE DataTable below it.
  const renderInterviewsWithReason = (reasonRow /* r */, parentRow) => {
    const interviewsRows = Array.isArray(reasonRow?.instances) ? reasonRow.instances : [];
    if (!interviewsRows.length) {
      return <Typography variant="body2" color="text.secondary">No interviews for this reason.</Typography>;
    }

    const iwColumns = [
      { key: 'name', header: 'Name', ...W.interviewsWithReason.name },
      { key: 'date', header: 'Date', type: 'date', ...W.interviewsWithReason.date },
      { key: 'consideredRaw', header: 'Considered Solution (raw)', ...W.interviewsWithReason.consideredRaw },
      { key: 'reason', header: 'Reason Not Chosen (raw)', ...W.interviewsWithReason.reason },
      {
        key: 'importance',
        header: 'Importance',
        type: 'number',
        align: 'right',
        accessor: (r) => (Number.isFinite(r?.importance) ? r.importance : null),
        render: (v) => (v != null ? F.reasonNotChosenImportance(v).text : '—'),
        ...W.interviewsWithReason.importance
      },
      { key: 'justification', header: 'Importance Justification', accessor: (r) => r.justification || '', ...W.interviewsWithReason.justification },
      {
        key: 'quote',
        header: 'Quote',
        accessor: (r) => r.quote || '',
        render: (text) =>
          text ? (
            <MuiLink
              href="#"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
              aria-label="Open quote clip"
            >
              <PlayCircleOutlineIcon fontSize="small" />
              <Typography component="span" variant="body2">{text}</Typography>
            </MuiLink>
          ) : '—',
        ...W.interviewsWithReason.quote
      },
      {
        key: 'href',
        header: 'Open',
        accessor: (r) => r.href || '',
        align: 'center',
        render: (href) =>
          href ? (
            <Tooltip title="Open interview">
              <IconButton
                size="small"
                component="a"
                href={href}
                target="_blank"
                rel="noopener"
                aria-label="Open interview"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : '—',
        ...W.interviewsWithReason.open
      }
    ];

    return (
      <Box sx={{ pt: 1 }}>
        <DataTable
          title="Interviews with this reason"
          columns={iwColumns}
          rows={interviewsRows}
          dense
        />
      </Box>
    );
  };

  /* -------- Sub-table #2: All interviews with this considered solution -------- */
  const allInterviewsColumns = [
    { key: 'name', header: 'Name', ...W.allInterviews.name },
    { key: 'date', header: 'Date', type: 'date', ...W.allInterviews.date },
    { key: 'consideredRaw', header: 'Considered Solution (raw)', ...W.allInterviews.consideredRaw },
    {
      key: 'reasonsList',
      header: 'Reasons Not Chosen',
      accessor: (r) => (Array.isArray(r.reasonsList) ? r.reasonsList.join(', ') : ''),
      ...W.allInterviews.reasonsList
    },
    {
      key: 'href',
      header: 'Open',
      accessor: (r) => r.href || '',
      align: 'center',
      render: (href) =>
        href ? (
          <Tooltip title="Open interview">
            <IconButton
              size="small"
              component="a"
              href={href}
              target="_blank"
              rel="noopener"
              aria-label="Open interview"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : '—',
      ...W.allInterviews.open
    }
  ];

  /* -------- Hook everything to the Table Kit parent -------- */
  const subTables = [
    {
      key: 'reasons',
      title: 'Reasons Not Chosen',
      columns: reasonsColumns,
      // 👇 NEW: show a completely separate table when a reason row is expanded
      renderExpanded: (reasonRow, parentRow) => renderInterviewsWithReason(reasonRow, parentRow)
    },
    {
      key: 'interviews',
      title: 'All interviews with this considered solution',
      columns: allInterviewsColumns
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={rows}
      subTables={subTables}
      dense
    />
  );
}
